import type { PendulumParams, SystemType } from './types.ts';

interface PNode { x: number; y: number }

// ====== SOA RING BUFFER (Structure of Arrays) ======

const MAX_NODES = 4;
const STATE_DIM = 8;

interface ClipBuffer {
  push(nodes: PNode[], state?: number[]): void;
  get(index: number): PNode[] | null;
  getState(index: number): number[] | null;
  nodeCountAt(index: number): number;
  clear(): void;
  readonly length: number;
  readonly xs: Float64Array[];
  readonly ys: Float64Array[];
  readonly nc: Uint8Array;
  readonly head: number;
}

function createClipBuffer(cap: number): ClipBuffer {
  const xs: Float64Array[] = [];
  const ys: Float64Array[] = [];
  for (let i = 0; i < MAX_NODES; i++) {
    xs.push(new Float64Array(cap));
    ys.push(new Float64Array(cap));
  }
  const nc = new Uint8Array(cap);
  const sxs: Float64Array[] = [];
  for (let i = 0; i < STATE_DIM; i++) sxs.push(new Float64Array(cap));
  const snc = new Uint8Array(cap);
  let head = 0;
  let _len = 0;

  const tmp: PNode[] = [];
  const tmpState: number[] = [];

  return {
    push(nodes: PNode[], state?: number[]) {
      const n = Math.min(nodes.length, MAX_NODES);
      for (let i = 0; i < n; i++) { xs[i][head] = nodes[i].x; ys[i][head] = nodes[i].y; }
      nc[head] = n;
      if (state) {
        const sn = Math.min(state.length, STATE_DIM);
        for (let i = 0; i < sn; i++) sxs[i][head] = state[i];
        snc[head] = sn;
      } else {
        snc[head] = 0;
      }
      head = (head + 1) % cap;
      if (_len < cap) _len++;
    },
    get(index: number): PNode[] | null {
      if (index < 0 || index >= _len) return null;
      const idx = (head - _len + index + cap) % cap;
      const n = nc[idx];
      tmp.length = n;
      for (let i = 0; i < n; i++) {
        (tmp[i] ??= { x: 0, y: 0 }).x = xs[i][idx];
        (tmp[i]).y = ys[i][idx];
      }
      return tmp.slice(0, n);
    },
    getState(index: number): number[] | null {
      if (index < 0 || index >= _len) return null;
      const idx = (head - _len + index + cap) % cap;
      const n = snc[idx];
      if (n === 0) return null;
      tmpState.length = n;
      for (let i = 0; i < n; i++) tmpState[i] = sxs[i][idx];
      return tmpState.slice(0, n);
    },
    nodeCountAt(index: number): number {
      if (index < 0 || index >= _len) return 0;
      return nc[(head - _len + index + cap) % cap];
    },
    clear() { head = 0; _len = 0; },
    get length() { return _len; },
    xs, ys, nc,
    get head() { return head; },
  };
}

// ====== RANGE SELECTOR ======

export type MarkerType = 'full' | 'half' | 'start';

export interface Marker {
  frame: number;
  type: MarkerType;
}

interface RangeSel {
  setTotal(total: number): void;
  getRange(): [number, number];
  setRange(s: number, e: number): void;
  addMarker(frame: number, type: MarkerType): void;
  clearMarkers(): void;
  getMarkers(): Marker[];
  onUpdate: ((s: number, e: number, markers: Marker[]) => void) | null;
}

function createRangeSelector(
  track: HTMLElement,
  thumbL: HTMLElement,
  thumbR: HTMLElement,
  highlight: HTMLElement,
  markersEl: HTMLElement,
): RangeSel {
  let total = 0;
  let left = 0;
  let right = 0;
  let leftDrag = false;
  let rightDrag = false;
  let trackRect: DOMRect | null = null;
  const markers: Marker[] = [];

  function updateVis() {
    if (total <= 0) return;
    const lo = total > 1 ? (left / (total - 1)) * 100 : 0;
    const hi = total > 1 ? (right / (total - 1)) * 100 : 100;
    thumbL.style.left = lo + '%';
    thumbR.style.left = hi + '%';
    highlight.style.left = lo + '%';
    highlight.style.width = Math.max(0, hi - lo) + '%';
  }

  function renderMarkers() {
    markersEl.innerHTML = '';
    if (total <= 1) return;
    for (const m of markers) {
      const el = document.createElement('div');
      el.className = `range-marker ${m.type}`;
      el.style.left = `${(m.frame / (total - 1)) * 100}%`;
      markersEl.appendChild(el);
    }
  }

  function idxFromX(clientX: number): number {
    const r = trackRect ?? track.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    return Math.round(frac * Math.max(0, total - 1));
  }

  thumbL.addEventListener('pointerdown', e => {
    e.preventDefault(); e.stopPropagation();
    leftDrag = true;
    trackRect = track.getBoundingClientRect();
    thumbL.setPointerCapture(e.pointerId);
  });
  thumbR.addEventListener('pointerdown', e => {
    e.preventDefault(); e.stopPropagation();
    rightDrag = true;
    trackRect = track.getBoundingClientRect();
    thumbR.setPointerCapture(e.pointerId);
  });

  thumbL.addEventListener('pointermove', e => {
    if (!leftDrag) return;
    left = Math.min(idxFromX(e.clientX), right);
    updateVis();
    rs.onUpdate?.(left, right, markers);
  });
  thumbR.addEventListener('pointermove', e => {
    if (!rightDrag) return;
    right = Math.max(idxFromX(e.clientX), left);
    updateVis();
    rs.onUpdate?.(left, right, markers);
  });

  const stopL = () => { leftDrag = false; };
  const stopR = () => { rightDrag = false; };
  thumbL.addEventListener('pointerup', stopL);
  thumbL.addEventListener('lostpointercapture', stopL);
  thumbR.addEventListener('pointerup', stopR);
  thumbR.addEventListener('lostpointercapture', stopR);

  const rs: RangeSel = {
    setTotal(t: number) {
      const prevMax = total > 0 ? total - 1 : 0;
      total = t;
      const newMax = t > 0 ? t - 1 : 0;
      if (right === prevMax) right = newMax;
      else if (right > newMax) right = newMax;
      if (left > newMax) left = newMax;
      updateVis();
      renderMarkers();
    },
    getRange(): [number, number] { return [left, right]; },
    setRange(s: number, e: number) {
      left = Math.max(0, Math.min(s, total > 0 ? total - 1 : 0));
      right = Math.max(left, Math.min(e, total > 0 ? total - 1 : 0));
      updateVis();
    },
    addMarker(frame: number, type: MarkerType) {
      const idx = markers.findIndex((m) => m.type === type);
      const clamped = Math.max(0, Math.min(frame, total > 0 ? total - 1 : 0));
      if (idx >= 0) markers[idx] = { frame: clamped, type };
      else markers.push({ frame: clamped, type });
      renderMarkers();
    },
    clearMarkers() {
      markers.length = 0;
      renderMarkers();
    },
    getMarkers(): Marker[] { return markers.slice(); },
    onUpdate: null,
  };

  return rs;
}

// ====== PENDULUM DRAWING (clip canvas) ======

const GOLD = 'rgba(169,130,74,1)';
const GOLD_BRIGHT = 'rgba(197,162,103,1)';
const BLUE = 'rgba(44,70,131,1)';

function scLike(sys: string): boolean {
  return sys === 'sculpture' || sys === 'resonantPendulum';
}

function armScaleFor(maxReach: number, cw: number, ch: number): number {
  return Math.min(110, (Math.min(cw, ch) / 2 - 10) / (maxReach * 1.15));
}

function transform(cw: number, ch: number, maxReach: number) {
  const s = armScaleFor(maxReach, cw, ch);
  const cx = cw / 2;
  const cy = ch / 2;
  return {
    tc: (x: number, y: number) => ({ px: cx + x * s, py: cy - y * s }),
    cx, cy,
  };
}

function drawDialBg(ctx: CanvasRenderingContext2D, cw: number, ch: number) {
  const cx = cw / 2, cy = ch / 2;
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, Math.max(cw, ch) * 0.75);
  grad.addColorStop(0, 'rgba(248,245,236,1)');
  grad.addColorStop(1, 'rgba(232,224,202,1)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, cw, ch);
}

function drawPendulumFrame(
  ctx: CanvasRenderingContext2D,
  cw: number, ch: number,
  nodes: PNode[],
  system: string,
  params: PendulumParams,
  maxReach: number,
) {
  if (nodes.length === 0) return;
  const { tc } = transform(cw, ch, maxReach);
  const p0 = tc(0, 0);

  if (!scLike(system) && nodes.length >= 2) {
    const p1 = tc(nodes[0].x, nodes[0].y);
    const p2 = tc(nodes[1].x, nodes[1].y);
    ctx.beginPath(); ctx.moveTo(p0.px, p0.py); ctx.lineTo(p1.px, p1.py);
    ctx.strokeStyle = 'rgba(90,100,133,0.85)'; ctx.lineWidth = 2; ctx.stroke();
    ctx.beginPath(); ctx.moveTo(p1.px, p1.py); ctx.lineTo(p2.px, p2.py);
    ctx.strokeStyle = GOLD; ctx.lineWidth = 2.2; ctx.stroke();

    const br1 = 2 + params.m1 * 2;
    const br2 = 2.5 + params.m2 * 2.5;
    ctx.beginPath(); ctx.arc(p1.px, p1.py, br1, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(110,58,40,0.65)'; ctx.fill();
    ctx.strokeStyle = 'rgba(133,96,49,0.8)'; ctx.lineWidth = 1; ctx.stroke();
    ctx.beginPath(); ctx.arc(p2.px, p2.py, br2, 0, Math.PI * 2);
    ctx.fillStyle = BLUE; ctx.fill();
    ctx.beginPath(); ctx.arc(p2.px, p2.py, br2 * 0.42, 0, Math.PI * 2);
    ctx.fillStyle = GOLD_BRIGHT; ctx.fill();

    ctx.beginPath(); ctx.arc(p0.px, p0.py, 3, 0, Math.PI * 2);
    ctx.fillStyle = GOLD; ctx.fill();
  } else {
    let prev = p0;
    for (let i = 0; i < nodes.length; i++) {
      const cur = tc(nodes[i].x, nodes[i].y);
      ctx.beginPath(); ctx.moveTo(prev.px, prev.py); ctx.lineTo(cur.px, cur.py);
      ctx.strokeStyle = i === nodes.length - 1 ? GOLD : 'rgba(90,100,133,0.8)';
      ctx.lineWidth = Math.max(1, nodes.length - i + 1);
      ctx.stroke();
      const rad = 3;
      ctx.beginPath(); ctx.arc(cur.px, cur.py, rad, 0, Math.PI * 2);
      ctx.fillStyle = i === nodes.length - 1 ? BLUE : 'rgba(110,58,40,0.75)';
      ctx.fill();
      prev = cur;
    }
    ctx.beginPath(); ctx.arc(p0.px, p0.py, 3, 0, Math.PI * 2);
    ctx.fillStyle = GOLD; ctx.fill();
  }
}

// ====== CLIPPER ======

export interface ClipperHandle {
  pushFrame(nodes: PNode[], state?: number[]): void;
  flush(): void;
  clear(): void;
  resize(): void;
  destroy(): void;
  getMarkers(): Marker[];
  getStateAt(frame: number): number[] | null;
}

interface ClipperOpts {
  getSystem: () => SystemType;
  getParams: () => PendulumParams;
  getDt: () => number;
  rangeTrack: HTMLElement;
  rangeThumbL: HTMLElement;
  rangeThumbR: HTMLElement;
  rangeHighlight: HTMLElement;
  rangeMarkers: HTMLElement;
  rangeTotalLabel: HTMLElement;
  panel: HTMLElement;
  canvas: HTMLCanvasElement;
  playBtn: HTMLButtonElement;
  speedSlider: HTMLInputElement;
  speedDisplay: HTMLElement;
  startDecBtn: HTMLButtonElement;
  startIncBtn: HTMLButtonElement;
  endDecBtn: HTMLButtonElement;
  endIncBtn: HTMLButtonElement;
  startDisplay: HTMLElement;
  endDisplay: HTMLElement;
  frameInfo: HTMLElement;
  closeBtn: HTMLButtonElement;
  labelFullBtn: HTMLButtonElement;
  labelHalfBtn: HTMLButtonElement;
  labelStartBtn: HTMLButtonElement;
  onRangeChange?: (s: number, e: number, markers: Marker[]) => void;
}

export function createClipper(opts: ClipperOpts): ClipperHandle {
  const CAP = 131072;
  const buf = createClipBuffer(CAP);
  const range = createRangeSelector(
    opts.rangeTrack, opts.rangeThumbL, opts.rangeThumbR, opts.rangeHighlight, opts.rangeMarkers,
  );

  const canvas = opts.canvas;
  const ctx = canvas.getContext('2d')!;
  const trailOff = document.createElement('canvas');
  const tctx = trailOff.getContext('2d')!;

  let curFrame = 0;
  let playing = false;
  let speed = 1;
  let panelVisible = false;
  let maxReach = 0.5;
  let animId: number | null = null;
  let lastTime = 0;
  let trailDirty = true;

  function bufIdx(frameIdx: number): number {
    return (buf.head - buf.length + frameIdx + CAP) % CAP;
  }

  function resizeCanvas() {
    const wrap = canvas.parentElement;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const cw = Math.floor(rect.width);
    const ch = Math.floor(rect.height);
    if (cw === 0 || ch === 0) return;
    canvas.width = cw; canvas.height = ch;
    trailOff.width = cw; trailOff.height = ch;
    trailDirty = true;
    if (panelVisible) drawFrame();
  }

  function computeMaxReach() {
    const [s, e] = range.getRange();
    maxReach = 0.1;
    for (let i = s; i <= e; i++) {
      const idx = bufIdx(i);
      const n = buf.nc[idx];
      for (let j = 0; j < n; j++) {
        const ax = buf.xs[j][idx] < 0 ? -buf.xs[j][idx] : buf.xs[j][idx];
        const ay = buf.ys[j][idx] < 0 ? -buf.ys[j][idx] : buf.ys[j][idx];
        if (ax > maxReach) maxReach = ax;
        if (ay > maxReach) maxReach = ay;
      }
    }
  }

  function drawTrail() {
    const [s, e] = range.getRange();
    const cw = trailOff.width;
    const ch = trailOff.height;
    tctx.clearRect(0, 0, cw, ch);
    if (s >= e || buf.length === 0) { trailDirty = false; return; }

    const scale = armScaleFor(maxReach, cw, ch);
    const cx = cw / 2;
    const cy = ch / 2;
    const system = opts.getSystem();
    const firstNc = buf.nc[bufIdx(s)];
    const tracked = scLike(system)
      ? Array.from({ length: firstNc }, (_, i) => i)
      : [firstNc - 1];

    tctx.lineCap = 'round';
    tctx.lineJoin = 'round';
    tctx.strokeStyle = 'rgba(44,70,131,0.78)';
    tctx.lineWidth = 1.6;

    for (const ti of tracked) {
      tctx.beginPath();
      let started = false;
      for (let i = s; i <= e; i++) {
        const idx = bufIdx(i);
        if (ti >= buf.nc[idx]) continue;
        const px = cx + buf.xs[ti][idx] * scale;
        const py = cy - buf.ys[ti][idx] * scale;
        if (!started) { tctx.moveTo(px, py); started = true; }
        else tctx.lineTo(px, py);
      }
      tctx.stroke();
    }
    trailDirty = false;
  }

  function drawFrame() {
    const cw = canvas.width;
    const ch = canvas.height;
    if (cw === 0 || ch === 0) return;
    if (trailDirty) drawTrail();

    ctx.clearRect(0, 0, cw, ch);
    drawDialBg(ctx, cw, ch);
    ctx.drawImage(trailOff, 0, 0);

    const nodes = buf.get(curFrame);
    if (nodes) {
      drawPendulumFrame(ctx, cw, ch, nodes, opts.getSystem(), opts.getParams(), maxReach);
    }

    const dt = opts.getDt();
    const [s, e] = range.getRange();
    const rangeLen = e - s + 1;
    opts.frameInfo.textContent =
      `Frame ${curFrame - s + 1} / ${rangeLen} \u00B7 t = ${(curFrame * dt).toFixed(3)}s`;
  }

  function showPanel() {
    if (panelVisible) return;
    panelVisible = true;
    opts.panel.style.display = '';
    resizeCanvas();
    computeMaxReach();
    trailDirty = true;
    curFrame = range.getRange()[0];
    drawFrame();
  }

  function hidePanel() {
    panelVisible = false;
    opts.panel.style.display = 'none';
    stopPlay();
  }

  function startPlay() {
    if (animId !== null) return;
    playing = true;
    opts.playBtn.textContent = '\u23F8 Pause';
    lastTime = performance.now();
    tick(lastTime);
  }

  function stopPlay() {
    playing = false;
    opts.playBtn.textContent = '\u25B6 Play';
    if (animId !== null) { cancelAnimationFrame(animId); animId = null; }
  }

  function tick(now: number) {
    if (!playing) return;
    const dt = opts.getDt();
    const elapsed = (now - lastTime) / 1000;
    lastTime = now;
    const frames = Math.max(1, Math.round(elapsed * speed / dt));
    const [s, e] = range.getRange();
    curFrame += frames;
    if (curFrame > e) curFrame = s;
    drawFrame();
    animId = requestAnimationFrame(tick);
  }

  function updateDisplays() {
    const [s, e] = range.getRange();
    opts.startDisplay.textContent = String(s);
    opts.endDisplay.textContent = String(e);
    opts.rangeTotalLabel.textContent = buf.length + ' frames';
  }

  function notifyRangeChange() {
    const [s, e] = range.getRange();
    opts.onRangeChange?.(s, e, range.getMarkers());
  }

  range.onUpdate = (_s, _e, _markers) => {
    notifyRangeChange();
    updateDisplays();
    const isSub = _s > 0 || _e < buf.length - 1;
    if (isSub && buf.length > 1) {
      showPanel();
      computeMaxReach();
      trailDirty = true;
      if (!playing) { curFrame = _s; drawFrame(); }
    }
  };

  opts.playBtn.addEventListener('click', () => {
    if (playing) stopPlay();
    else {
      const [s, e] = range.getRange();
      if (curFrame < s || curFrame > e) curFrame = s;
      startPlay();
    }
  });

  opts.speedSlider.addEventListener('input', () => {
    speed = parseFloat(opts.speedSlider.value);
    opts.speedDisplay.textContent = speed.toFixed(1) + 'x';
  });

  function nudgeStart(d: number) {
    const [s, e] = range.getRange();
    const ns = Math.max(0, Math.min(s + d, e));
    if (ns === s) return;
    range.setRange(ns, e);
    notifyRangeChange();
    updateDisplays();
    computeMaxReach();
    trailDirty = true;
    if (!playing) { curFrame = Math.max(curFrame, ns); drawFrame(); }
  }

  function nudgeEnd(d: number) {
    const [s, e] = range.getRange();
    const ne = Math.max(s, Math.min(e + d, buf.length - 1));
    if (ne === e) return;
    range.setRange(s, ne);
    notifyRangeChange();
    updateDisplays();
    computeMaxReach();
    trailDirty = true;
    if (!playing && curFrame > ne) { curFrame = ne; drawFrame(); }
    else if (!playing) drawFrame();
  }

  opts.startDecBtn.addEventListener('click', () => nudgeStart(-1));
  opts.startIncBtn.addEventListener('click', () => nudgeStart(1));
  opts.endDecBtn.addEventListener('click', () => nudgeEnd(-1));
  opts.endIncBtn.addEventListener('click', () => nudgeEnd(1));

  function labelOrbit(type: 'full' | 'half') {
    if (buf.length < 2) return;
    const [s, e] = range.getRange();
    const frame = e;
    range.addMarker(frame, type);
    range.setRange(s, frame);
    notifyRangeChange();
    updateDisplays();
    computeMaxReach();
    trailDirty = true;
    if (panelVisible) {
      if (curFrame < s || curFrame > frame) curFrame = s;
      drawFrame();
    } else {
      const isSub = s > 0 || frame < buf.length - 1;
      if (isSub && buf.length > 1) showPanel();
    }
  }

  opts.labelFullBtn.addEventListener('click', () => labelOrbit('full'));
  opts.labelHalfBtn.addEventListener('click', () => labelOrbit('half'));

  function labelStart() {
    if (buf.length < 2) return;
    const [s, _e] = range.getRange();
    range.addMarker(s, 'start');
    notifyRangeChange();
    updateDisplays();
    if (panelVisible) drawFrame();
  }

  opts.labelStartBtn.addEventListener('click', labelStart);

  opts.closeBtn.addEventListener('click', hidePanel);

  let lastSyncLen = 0;

  return {
    pushFrame(nodes: PNode[], state?: number[]) {
      buf.push(nodes, state);
    },
    flush() {
      if (buf.length !== lastSyncLen) {
        range.setTotal(buf.length);
        opts.rangeTotalLabel.textContent = buf.length + ' frames';
        notifyRangeChange();
        lastSyncLen = buf.length;
      }
    },
    clear() {
      buf.clear();
      range.setTotal(0);
      range.clearMarkers();
      notifyRangeChange();
      stopPlay();
      trailDirty = true;
      curFrame = 0;
      hidePanel();
    },
    resize() { resizeCanvas(); },
    destroy() { stopPlay(); hidePanel(); },
    getMarkers() { return range.getMarkers(); },
    getStateAt(frame: number) { return buf.getState(frame); },
  };
}
